import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, useTheme, type Theme } from '@mui/material/styles';

/**
 * SalesModal root is raised so it clears leftover page overlays.
 * Anything that portals with the default `theme.zIndex.modal` would then
 * render BEHIND the sales panel (Dialog, Select menus, Autocomplete,
 * DatePicker poppers, etc.).
 *
 * Fix: wrap SalesModal content in {@link SalesModalNestedTheme} so every
 * nested overlay inherits an elevated `zIndex.modal` automatically.
 * Do not raise SalesModal further without raising this nested offset.
 */
export const SALES_MODAL_ROOT_Z_OFFSET = 10;

/** Nested overlays must clear the SalesModal root (outer.modal + ROOT offset). */
export const SALES_NESTED_MODAL_Z_OFFSET = 20;

/**
 * Explicit Dialog sx when a Dialog might render outside the nested theme
 * (or as belt-and-suspenders). Prefer relying on {@link SalesModalNestedTheme}.
 */
export const salesNestedDialogSx = (theme: Theme) => ({
  zIndex: theme.zIndex.modal,
});

/** Absolute override for ConfirmationModal siblings rendered outside SalesModal. */
export const SALES_CONFIRMATION_DIALOG_Z_INDEX = 1410;

export const salesModalRootSx = (theme: Theme) => ({
  zIndex: theme.zIndex.modal + SALES_MODAL_ROOT_Z_OFFSET,
});

export function createSalesModalNestedTheme(outer: Theme): Theme {
  return createTheme(outer, {
    zIndex: {
      modal: outer.zIndex.modal + SALES_NESTED_MODAL_Z_OFFSET,
    },
  });
}

/**
 * Elevates MUI modal stacking for all descendants (including portaled Dialog,
 * Menu, Select, Popper, DatePicker). Place immediately inside SalesModal.
 */
export const SalesModalNestedTheme: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const outer = useTheme();
  const nested = useMemo(() => createSalesModalNestedTheme(outer), [outer]);
  return <ThemeProvider theme={nested}>{children}</ThemeProvider>;
};
